import pick from 'lodash/pick';
import {
  createStripeAccount,
  updateStripeAccount,
  fetchStripeAccount,
} from '../../ducks/stripeConnectAccount.duck';
import { fetchCurrentUser } from '../../ducks/user.duck';
import { addOrUpdateAPerson } from '../../custom-ducks/pipedrive.duck';
import axios from 'axios';
import config from '../../config';
import { getBearerToken } from '../../util/cookie';
import { checkProfileCompleted } from '../../custom-ducks/utility.duck';

// ================ Action types ================ //

export const SET_INITIAL_VALUES = 'app/StripePayoutPage/SET_INITIAL_VALUES';
export const SAVE_PAYOUT_DETAILS_REQUEST = 'app/StripePayoutPage/SAVE_PAYOUT_DETAILS_REQUEST';
export const SAVE_PAYOUT_DETAILS_SUCCESS = 'app/StripePayoutPage/SAVE_PAYOUT_DETAILS_SUCCESS';
export const SAVE_PAYOUT_DETAILS_ERROR = 'app/StripePayoutPage/SAVE_PAYOUT_DETAILS_ERROR';

// ================ Reducer ================ //

const initialState = {
  payoutDetailsSaveInProgress: false,
  payoutDetailsSaved: false,
  fromReturnURL: false,
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case SET_INITIAL_VALUES:
      return { ...initialState, ...payload };

    case SAVE_PAYOUT_DETAILS_REQUEST:
      return { ...state, payoutDetailsSaveInProgress: true };
    case SAVE_PAYOUT_DETAILS_ERROR:
      return { ...state, payoutDetailsSaveInProgress: false };
    case SAVE_PAYOUT_DETAILS_SUCCESS:
      return { ...state, payoutDetailsSaveInProgress: false, payoutDetailsSaved: true };

    default:
      return state;
  }
}

// ================ Action creators ================ //

export const setInitialValues = initialValues => ({
  type: SET_INITIAL_VALUES,
  payload: pick(initialValues, Object.keys(initialState)),
});

export const savePayoutDetailsRequest = () => ({
  type: SAVE_PAYOUT_DETAILS_REQUEST,
});
export const savePayoutDetailsError = () => ({
  type: SAVE_PAYOUT_DETAILS_ERROR,
});
export const savePayoutDetailsSuccess = () => ({
  type: SAVE_PAYOUT_DETAILS_SUCCESS,
});

// ================ Thunks ================ //

export const savePayoutDetails = (values, isUpdateCall) => (dispatch, getState, sdk) => {
  const upsertThunk = isUpdateCall ? updateStripeAccount : createStripeAccount;
  dispatch(savePayoutDetailsRequest());

  return dispatch(upsertThunk(values, { expand: true }))
    .then(response => {
      dispatch(savePayoutDetailsSuccess())
      // dispatch(createOrUpdateContact({
      //   has_added_stripe_account: true,
      // }))
      dispatch(addOrUpdateAPerson())
      dispatch(checkProfileCompleted())

      return response;
    })
    .catch(() => dispatch(savePayoutDetailsError()));
};

export const loadData = () => (dispatch, getState, sdk) => {
  // Clear state so that previously loaded data is not visible
  // in case this page load fails.
  dispatch(setInitialValues());

  return dispatch(fetchCurrentUser()).then(response => {
    const currentUser = getState().user.currentUser;
    if (currentUser && currentUser.stripeAccount) {
      dispatch(fetchStripeAccount());
    }
    return response;
  });
};

export const removeSubscription = () => async (dispatch, getState, sdk) => {
  return axios.post(`${config.subscriptionServiceUrl}chargebee/remove-subscription`, {}, {
    headers: {
      'content-type': 'application/json',
      Authorization: await getBearerToken(sdk),
    },
  }).then(() => {
    dispatch(fetchCurrentUser())
  })
};

/**
 * Un-schedules a pending cancellation on the user's existing subscription.
 *
 * Backend (subscription-service /chargebee/reactivate-subscription) calls
 * Chargebee's `remove_scheduled_cancellation` and clears the
 * `subscriptionCancellation` flag on the Sharetribe user's metadata.
 * After the request returns we re-fetch the current user so the FTW page
 * re-renders without the "Cancellation scheduled" badge and with the
 * Cancel Subscription button visible again.
 *
 * Throws on HTTP failure so the caller can surface the error in the UI.
 */
export const reactivateSubscription = () => async (dispatch, getState, sdk) => {
  await axios.post(
    `${config.subscriptionServiceUrl}chargebee/reactivate-subscription`,
    {},
    {
      headers: {
        'content-type': 'application/json',
        Authorization: await getBearerToken(sdk),
      },
    }
  );
  await dispatch(fetchCurrentUser());
};

/**
 * Requests a Chargebee customer-portal session for the authenticated user.
 * Returns the portal_session object that Chargebee.js needs to open the
 * iframe. Throws on failure so the caller can fall back gracefully.
 */
export const fetchPortalSession = () => async (dispatch, getState, sdk) => {
  const response = await axios.post(
    `${config.subscriptionServiceUrl}chargebee/portal-session`,
    {},
    {
      headers: {
        'content-type': 'application/json',
        Authorization: await getBearerToken(sdk),
      },
    }
  );
  return response.data;
};
